import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, Clock, ArrowRight, Lock, FileText } from 'lucide-react';
import { useSiteSettings, useBookingActive } from '@/contexts/SiteSettingsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Servizi() {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const canSeePrices = user && (user.mostra_prezzi || user.role === 'admin' || user.role === 'operator');
  const siteSettings = useSiteSettings();
  const bookingActive = useBookingActive();
  const globalPrices = siteSettings.mostra_prezzi_globale === true;
  const showPrices = globalPrices && canSeePrices;

  useEffect(() => {
    const params = showPrices ? { include_prices: true } : {};
    axios.get(`${API}/services`, { params })
      .then(r => setServices(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const categories = useMemo(() => [...new Set(services.map(s => s.categoria))], [services]);

  return (
    <div>
      <section className="hero-wash py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F2A37] mb-4" style={{fontFamily:'Space Grotesk'}}>I Nostri Servizi</h1>
          <p className="text-lg text-muted-foreground max-w-3xl">Scopri la gamma completa dei nostri servizi medici specialistici</p>
        </div>
      </section>

      <section className="py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {false && (
            <div className="flex items-center gap-2 bg-[hsl(var(--accent))] rounded-lg px-4 py-3 mb-8">
              <Lock size={16} className="text-[#238A92]" />
              <p className="text-sm text-[hsl(var(--accent-foreground))]">
                I prezzi delle prestazioni sono disponibili per i pazienti registrati. <Link to="/login" className="text-[#238A92] font-medium hover:underline">Accedi</Link> o <Link to="/registrati" className="text-[#238A92] font-medium hover:underline">registrati</Link> per visualizzarli.
              </p>
            </div>
          )}

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}
            </div>
          ) : categories.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">Nessun servizio disponibile al momento.</p>
          ) : (
            categories.map(cat => (
              <div key={cat} className="mb-12">
                <h2 className="text-2xl font-bold text-[#1F2A37] mb-6" style={{fontFamily:'Space Grotesk'}}>{cat}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {services.filter(s => s.categoria === cat).map(service => (
                    <Card key={service.id} className="group hover:shadow-[var(--shadow-md)] hover:border-[rgba(35,138,146,.35)] transition-shadow transition-colors duration-200">
                      <Link to={`/servizi/${service.id}`}>
                        {service.immagine_url && (
                          <div className="aspect-video overflow-hidden rounded-t-xl bg-[hsl(var(--muted))]">
                            <img src={service.immagine_url} alt={service.nome} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                          </div>
                        )}
                      </Link>
                      <CardContent className="p-6">
                        <Link to={`/servizi/${service.id}`}>
                          <h3 className="text-lg font-semibold text-[#1F2A37] mb-2 hover:text-[#238A92] transition-colors" style={{fontFamily:'Space Grotesk'}}>{service.nome}</h3>
                        </Link>
                        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{service.descrizione}</p>
                        <div className="flex items-center gap-3 mb-4">
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <Clock size={12} /> {service.durata_minuti} min
                          </Badge>
                          {showPrices && service.prezzo && (
                            <Badge variant="secondary">
                              {service.prezzo.toFixed(2)} EUR
                            </Badge>
                          )}
                          {service.documento_url && (
                            <Badge variant="secondary" className="flex items-center gap-1 text-red-600">
                              <FileText size={12} /> PDF
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Link to={`/servizi/${service.id}`} className="flex-1">
                            <Button size="sm" variant="outline" className="w-full text-sm">
                              Dettaglio <ArrowRight size={14} className="ml-1" />
                            </Button>
                          </Link>
                          {bookingActive && (
                            <Link to={`/prenota?service=${service.id}`}>
                              <Button size="sm" className="text-sm bg-[#238A92] hover:bg-[#0F5E66] text-white" aria-label={`Prenota ${service.nome}`} data-testid={`service-book-btn-${service.id}`}>
                                <Calendar size={14} />
                              </Button>
                            </Link>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
